import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  FormControlLabel,
  Checkbox,
  FormGroup,
} from "@mui/material";
import DialogContainer from "../../../../../../components/dialog-container/dialog-container";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  getServicesRequest,
  getInspectionServicesRequest,
  getCurrentServicesRequest,
} from "../../../../../../redux/inspection-firms/slice";
import Loading from "../../../../../../components/loading/loading";
import ErrorNotification from "../../../../../../components/error-notification/error-notification";
import {
  updateCurrentServicesDetailsRequest,
  updateServicesDetailsRequest,
} from "../../../../../../utils/APICalls";

type props = {
  showDialog: boolean;
  closeDialog: any;
  data?: any;
};

interface FormValues {
  agencyInspections: string[];
  commercialInspections: string[];
  residentialInspections: string[];
  specialtyInspections: string[];
}

export default function EditServices({ showDialog, closeDialog, data }: props) {
  const dispatch = useDispatch();
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { inspectionServices, loading, error } = useSelector(
    (state: any) => state.inspectionFirms
  );

  useEffect(() => {
    dispatch(getInspectionServicesRequest());
  }, [dispatch]);

  const ValidationSchema = yup.object({
    agencyInspections: yup
      .array()
      .of(yup.string())
      .test(
        "at-least-one-inspection",
        "This field can't be empty",
        function (value) {
          const agency = value || [];
          const banking = (this.parent?.commercialInspections as string[]) || [];
          const residential = (this.parent?.residentialInspections as string[]) || [];
          const specialty = (this.parent?.specialtyInspections as string[]) || [];
          return agency.length > 0 || banking.length > 0 || residential.length > 0 || specialty.length > 0;
        }
      ),
    commercialInspections: yup
      .array()
      .of(yup.string())
      .test(
        "at-least-one-inspection",
        "This field can't be empty",
        function (value) {
          const banking = value || [];
          const agency = (this.parent?.agencyInspections as string[]) || [];
          const residential = (this.parent?.residentialInspections as string[]) || [];
          const specialty = (this.parent?.specialtyInspections as string[]) || [];
          return banking.length > 0 || agency.length > 0 || residential.length > 0 || specialty.length > 0;
        }
      ),
    residentialInspections: yup
      .array()
      .of(yup.string())
      .test(
        "at-least-one-inspection",
        "This field can't be empty",
        function (value) {          const residential = value || [];
          const agency = (this.parent?.agencyInspections as string[]) || [];
          const banking = (this.parent?.commercialInspections as string[]) || [];
          const specialty = (this.parent?.specialtyInspections as string[]) || [];
          return residential.length > 0 || agency.length > 0 || banking.length > 0 || specialty.length > 0;
        }
      ),
    specialtyInspections: yup
      .array()
      .of(yup.string())
      .test(
        "at-least-one-inspection",
        "This field can't be empty",
        function (value) {
          const specialty = value || [];
          const agency = (this.parent?.agencyInspections as string[]) || [];
          const banking = (this.parent?.commercialInspections as string[]) || [];
          const residential = (this.parent?.residentialInspections as string[]) || [];
          return specialty.length > 0 || agency.length > 0 || banking.length > 0 || residential.length > 0;
        }
      ),
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      agencyInspections:
        data?.agencyInspections?.map((type: any) => type.inspectionType) || [],
      commercialInspections:
        data?.commercialInspections?.map((type: any) => type.inspectionType) || [],
      residentialInspections:
        data?.residentialInspections?.map((type: any) => type.inspectionType) || [],
      specialtyInspections:
        data?.specialtyInspections?.map((type: any) => type.inspectionType) || []
    },
    validationSchema: ValidationSchema,
    onSubmit: async (values) => {
      const isValid = await formik.validateForm();
      if (!isValid) {
        formik.setTouched({
          agencyInspections: true,
          commercialInspections: true,
          residentialInspections: true,
          specialtyInspections: true,
        });
        return;
      }
      if (data?.id) {
        updateServicesDetails(values);
      } else {
        updateCurrentServicesDetails(values);
      }
    },
    enableReinitialize: true,
  });

  const updateServicesDetails = async (values: any) => {
    const payload = {
      id: data?.id,
      inspectionTypes: [
        ...values.agencyInspections,
        ...values.commercialInspections,
        ...values.residentialInspections,
        ...values.specialtyInspections,
      ],
    };
    setShowLoading(true);
    const response = await updateServicesDetailsRequest(payload);
    setShowLoading(false);
    if (response?.status === 204) {
      dispatch(getServicesRequest({ id: data?.id }));
      hideDialog();
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const updateCurrentServicesDetails = async (values: any) => {
    const payload = {
      inspectionTypes: [
        ...values.agencyInspections,
        ...values.commercialInspections,
        ...values.residentialInspections,
        ...values.specialtyInspections,
      ],
    };
    setShowLoading(true);
    const response = await updateCurrentServicesDetailsRequest(payload);
    setShowLoading(false);
    if (response?.status === 204) {
      dispatch(getCurrentServicesRequest());
      hideDialog();
    } else if (response?.status === 400 || response?.status === 404) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const hideDialog = () => {
    formik.resetForm();
    closeDialog();
  };

  return (
    <DialogContainer
      title="Inspection Firm - Services Offered"
      showDialog={showDialog}
      closeDialog={closeDialog}
      onSubmit={formik.handleSubmit}
    >
      <form onSubmit={formik.handleSubmit}>
        <Box className="user-name-layout">
          <Typography className="label-txt">Agency Inspections</Typography>
          <FormGroup>
            {inspectionServices
              .filter((type: any) => type.category === "Agency")
              .map((type: any) => (
                <FormControlLabel
                  key={type.inspectionType}
                  control={
                    <Checkbox
                      checked={formik.values.agencyInspections?.includes(
                        type.inspectionType
                      )}
                      onBlur={() =>
                        formik.setFieldTouched("agencyInspections", true)
                      }
                      onChange={() => {
                        const set = new Set(formik.values.agencyInspections);
                        if (set.has(type.inspectionType)) {
                          set.delete(type.inspectionType);
                        } else {
                          set.add(type.inspectionType);
                        }
                        formik.setFieldValue(
                          "agencyInspections",
                          Array.from(set)
                        );
                      }}
                      name={type.inspectionType}
                    />
                  }
                  label={type.displayName}
                />
              ))}
            {formik.touched.agencyInspections &&
              formik.errors.agencyInspections && (
                <Typography color="error" variant="caption">
                  {formik.errors.agencyInspections}
                </Typography>
              )}
          </FormGroup>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">
            Commercial Property
            <br />
            Inspections
          </Typography>
          <FormGroup>
            {inspectionServices
              .filter((type: any) => type.category === "Commercial")
              .map((type: any) => (
                <FormControlLabel
                  key={type.inspectionType}
                  control={
                    <Checkbox
                      checked={formik.values.commercialInspections?.includes(
                        type.inspectionType
                      )}
                      onBlur={() =>
                        formik.setFieldTouched("commercialInspections", true)
                      }
                      onChange={() => {
                        const set = new Set(formik.values.commercialInspections);
                        if (set.has(type.inspectionType)) {
                          set.delete(type.inspectionType);
                        } else {
                          set.add(type.inspectionType);
                        }
                        formik.setFieldValue(
                          "commercialInspections",
                          Array.from(set)
                        );
                      }}
                      name={type.inspectionType}
                    />
                  }
                  label={type.displayName}
                />
              ))}
            {formik.touched.commercialInspections &&
              formik.errors.commercialInspections && (
                <Typography color="error" variant="caption">
                  {formik.errors.commercialInspections}
                </Typography>
              )}
          </FormGroup>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">
            Residential Property
            <br />
            Inspections
          </Typography>
          <FormGroup>
            {inspectionServices
              .filter((type: any) => type.category === "Residential")
              .map((type: any) => (
                <FormControlLabel
                  key={type.inspectionType}
                  control={
                    <Checkbox
                      checked={formik.values.residentialInspections?.includes(
                        type.inspectionType
                      )}
                      onBlur={() =>
                        formik.setFieldTouched("residentialInspections", true)
                      }
                      onChange={() => {
                        const set = new Set(formik.values.residentialInspections);
                        if (set.has(type.inspectionType)) {
                          set.delete(type.inspectionType);
                        } else {
                          set.add(type.inspectionType);
                        }
                        formik.setFieldValue(
                          "residentialInspections",
                          Array.from(set)
                        );
                      }}
                      name={type.inspectionType}
                    />
                  }
                  label={type.displayName}
                />
              ))}
            {formik.touched.residentialInspections &&
              formik.errors.residentialInspections && (
                <Typography color="error" variant="caption">
                  {formik.errors.residentialInspections}
                </Typography>
              )}
          </FormGroup>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">
            Specialty Inspections
          </Typography>
          <FormGroup>
            {inspectionServices
              .filter((type: any) => type.category === "Specialty")
              .map((type: any) => (
                <FormControlLabel
                  key={type.inspectionType}
                  control={
                    <Checkbox
                      checked={formik.values.specialtyInspections?.includes(
                        type.inspectionType
                      )}
                      onBlur={() =>
                        formik.setFieldTouched("specialtyInspections", true)
                      }
                      onChange={() => {
                        const set = new Set(formik.values.specialtyInspections);
                        if (set.has(type.inspectionType)) {
                          set.delete(type.inspectionType);
                        } else {
                          set.add(type.inspectionType);
                        }
                        formik.setFieldValue(
                          "specialtyInspections",
                          Array.from(set)
                        );
                      }}
                      name={type.inspectionType}
                    />
                  }
                  label={type.displayName}
                />
              ))}
            {formik.touched.specialtyInspections &&
              formik.errors.specialtyInspections && (
                <Typography color="error" variant="caption">
                  {formik.errors.specialtyInspections}
                </Typography>
              )}
          </FormGroup>
        </Box>
        <Typography
          sx={{
            color: "rgba(0, 0, 0, 0.6) !important",
            mt: 2,
            fontStyle: "italic",
            fontSize: "14px",
          }}
        >
          *In order to receive these types of inspections, at least 1 certified
          inspector must be active in your firm.
        </Typography>
      </form>
      {(loading || showLoading) && <Loading />}
      {(error || showErrorNotification) && (
        <ErrorNotification
          show={showErrorNotification}
          message={error || errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}
    </DialogContainer>
  );
}
